import diacritics from 'diacritics';

import { TrueTime } from '../time/true-time.js';
import { EventUtils } from './event-utils.js';
import { isNotNullOrUndefined, isNullOrUndefined } from './null-safety.js';
import { isNullOrUndefinedOrEmpty } from './string.js';
import { Artist, ArtistList, Brand, BrandList, Event, EventList, KeywordList, KeywordRef, Organizer, OrganizerList, SearchSnapshot, SnapshotElement } from '../models/serializable/search-snapshot.js';
import { OrganizerProfile } from '../models/serializable/organizer-profile.js';
import { ArtistProfile } from '../models/serializable/artist-profile.js';
import { BrandProfile } from '../models/serializable/brand-profile.js';
import { EventProfile } from '../models/serializable/event-profile.js';
import { ArtistInfo } from '../models/serializable/artist-info.js';
import { BrandInfo } from '../models/serializable/brand-info.js';
import { OrganizerInfo } from '../models/serializable/organizer-info.js';
import { ITicketPool } from '../models/serializable/ticketing-state/ticket-pool.js';
import { DynamicPricingSettings } from '../models/serializable/dynamic-pricing-settings.js';
import { SeatingLayout } from '../models/serializable/seating/seating-layout.js';
import { EventSeatSelectionLock } from '../models/serializable/ticketing-state/event-seat-selection-lock.js';
import { TicketSupplementTemplate } from '../models/serializable/ticket-supplement-template.js';
import { Country } from '../models/serializable/country.js';
import { LocalizedString } from '../models/serializable/localized-string.js';
import { LocalizedNameComponent } from '../components/point-of-interest/localized-name-component.js';
import { LocalizedProfilePhotosComponent } from '../components/business/localized-profile-photos-component.js';
import { PublicProfileClassificationComponent } from '../components/public-profile/public-profile-classification-component.js';
import { AsciiString120, AsciiString20 } from '../models/core/string.js';
import { u16, u8 } from '../models/core/int.js';
import { EventPrivacyMode } from '../models/serializable/event-privacy-mode.js';
import { Language } from '../models/serializable/language.js';

export interface EventData {
  event: EventProfile,
  ticketPool: ITicketPool | null,
  dynamicPricingSettings: DynamicPricingSettings | null,
  seatingLayout: SeatingLayout | null,
  seatSelectionLock: EventSeatSelectionLock | null,
  ticketSupplementTemplates: TicketSupplementTemplate[] | null,
}

export function publicProfilesIntoInfos({ events, artists, brands, organizers }: {
  events: EventProfile[],
  artists: ArtistProfile[],
  brands: BrandProfile[],
  organizers: OrganizerProfile[],
}): {
  artists: ArtistInfo[],
  brands: BrandInfo[],
  organizers: OrganizerInfo[],
} {
  const associationsTable = new Map<string, {eventIds: Set<string>, countries: Set<Country> }>();
  for(const event of events) {
    const associations = event.associations;

    for(const artistId of associations.artists) {
      if(!associationsTable.has(artistId)) {
        associationsTable.set(artistId, { eventIds: new Set<string>(), countries: new Set<Country>() });
      }

      const entry = associationsTable.get(artistId)!;
      entry.eventIds.add(event.uniqueId);
      entry.countries.add(event.country);
    }

    for(const brandId of associations.brands) {
      if(!associationsTable.has(brandId)) {
        associationsTable.set(brandId, { eventIds: new Set<string>(), countries: new Set<Country>() });
      }

      const entry = associationsTable.get(brandId)!;
      entry.eventIds.add(event.uniqueId);
      entry.countries.add(event.country);
    }

    for(const organizerId of associations.organizers) {
      if(!associationsTable.has(organizerId)) {
        associationsTable.set(organizerId, { eventIds: new Set<string>(), countries: new Set<Country>() });
      }

      const entry = associationsTable.get(organizerId)!;
      entry.eventIds.add(event.uniqueId);
      entry.countries.add(event.country);
    }
  }

  const artistInfos = new Array<ArtistInfo>();
  for(const artist of artists) {
    const entry = associationsTable.get(artist.uniqueId);
    if(entry == null) {
      continue;
    }

    const args = profileIntoInfoArgs(artist, entry);
    if(args == null) {
      continue;
    }

    const artistInfo = ArtistInfo.create(args);
    artistInfos.push(artistInfo);
  }

  const brandInfos = new Array<BrandInfo>();
  for(const brand of brands) {
    const entry = associationsTable.get(brand.uniqueId);
    if(entry == null) {
      continue;
    }

    const args = profileIntoInfoArgs(brand, entry);
    if(args == null) {
      continue;
    }

    const brandInfo = BrandInfo.create(args);
    brandInfos.push(brandInfo);
  }

  const organizerInfos = new Array<OrganizerInfo>();
  for(const organizer of organizers) {
    const entry = associationsTable.get(organizer.uniqueId);
    if(entry == null) {
      continue;
    }

    const args = profileIntoInfoArgs(organizer, entry);
    if(args == null) {
      continue;
    }

    const organizerInfo = OrganizerInfo.create(args);
    organizerInfos.push(organizerInfo);
  }

  return {
    artists: artistInfos,
    brands: brandInfos,
    organizers: organizerInfos,
  };
}

export function profileIntoInfoArgs(
    profile: ArtistProfile | BrandProfile | OrganizerProfile,
    entry: {eventIds: Set<string>, countries: Set<Country>,
}): {
  localizedName: LocalizedString,
  uniqueId: string,
  webRoute: string,
  coverImage: string,
  country: Country,
  eventCount: number,
  isFeatured: boolean,
  featuredScore: number,
} | null {
  const localizedName = profile.findComponentField<LocalizedNameComponent, LocalizedString>(
      LocalizedNameComponent.TYPE_ID,
      (c) => c.names,
  );

  if(localizedName == null) {
    return null;
  }

  const webRoute = profile.webRoute;
  if(isNullOrUndefined(webRoute)) {
    return null;
  }

  const coverImage = profile.findComponentField<LocalizedProfilePhotosComponent, string | null>(
      LocalizedProfilePhotosComponent.TYPE_ID,
      (c) => c.portfolio.photos.at(0)?.getAny()?.downloadUrl ?? null,
  );

  if(coverImage == null) {
    return null;
  }

  const country = entry.countries.keys().next().value;
  if(country == null) {
    return null;
  }

  const eventCount = entry.eventIds.size;
  if(eventCount < 1) {
    return null;
  }

  const isFeatured = profile.findComponentField<PublicProfileClassificationComponent, boolean>(
      PublicProfileClassificationComponent.TYPE_ID,
      (c) => c.isFeatured,
  ) ?? false;
  const featuredScore = profile.findComponentField<PublicProfileClassificationComponent, number>(
      PublicProfileClassificationComponent.TYPE_ID,
      (c) => c.featuredScore,
  ) ?? 0;

  return {
    uniqueId: profile.uniqueId,
    localizedName,
    webRoute,
    coverImage,
    eventCount,
    country,
    isFeatured,
    featuredScore,
  };
}

export function generateSearchSnapshot({ eventProfiles, artistProfiles, brandProfiles, organizerProfiles }: {
  eventProfiles: EventProfile[],
  artistProfiles: ArtistProfile[],
  brandProfiles: BrandProfile[],
  organizerProfiles: OrganizerProfile[],
}): SearchSnapshot {
  const eventNameKeywordsListId = u8.make(1);
  const eventNameKeywordsList: AsciiString120[] = [];
  let eventNameKeywordListIndex = -1;

  const eventListEvents: Event[] = [];

  for(const event of eventProfiles) {
    const privacyMode = EventUtils.findPrivacyMode(event);
    if(isNullOrUndefined(privacyMode) || privacyMode !== EventPrivacyMode.Public) {
      continue;
    }

    const now = TrueTime.now();
    const endDate = EventUtils.findEndDate(event);
    if(isNotNullOrUndefined(endDate) && endDate.isBefore(now)) {
      continue;
    }

    const name = EventUtils.findName(event);
    if(isNullOrUndefined(name)) {
      continue;
    }

    const keywords = generateKeywordsFromLocalizedString(name);
    if(keywords.length <= 0) {
      continue;
    }

    const searchSnapshotEventKeywordRefs: KeywordRef[] = [];

    for(const keyword of keywords) {
      eventNameKeywordListIndex++;
      eventNameKeywordsList.push(keyword);

      searchSnapshotEventKeywordRefs.push(KeywordRef.create({
        listId: eventNameKeywordsListId,
        index: u16.make(eventNameKeywordListIndex),
      }));
    }

    eventListEvents.push(Event.create({
      uniqueId: AsciiString20.make(event.uniqueId),
      keywords: searchSnapshotEventKeywordRefs,
    }));
  }

  const [
    artistSearchElements,
    brandSearchElements,
    organizerSearchElements,
  ] = [
    generateArtistSearchElements(artistProfiles),
    generateBrandSearchElements(brandProfiles),
    generateOrganizerSearchElements(organizerProfiles),
  ];

  const searchSnapshot = SearchSnapshot.create({
    elements: [
      KeywordList.create({
        listId: eventNameKeywordsListId,
        keywords: eventNameKeywordsList,
      }),
      EventList.create({
        events: eventListEvents,
      }),
      ...artistSearchElements,
      ...brandSearchElements,
      ...organizerSearchElements,
    ],
  });

  return searchSnapshot;
}

export function generateArtistSearchElements(artistProfiles: ArtistProfile[]): SnapshotElement[] {
  const artistList = new Array<Artist>();
  const keywordList = new Array<AsciiString120>();
  const keywordListId = u8.make(2);
  let keywordsListIndex = -1;

  for(const artist of artistProfiles) {
    const name = artist.findComponentField<LocalizedNameComponent, LocalizedString>(
        LocalizedNameComponent.TYPE_ID, (c) => c.names);

    if(name == null) {
      continue;
    }

    const artistKeywordRefs = new Array<KeywordRef>();

    const keywords = generateKeywordsFromLocalizedString(name);
    for(const keyword of keywords) {
      keywordsListIndex++;
      keywordList.push(keyword);

      artistKeywordRefs.push(KeywordRef.create({
        listId: keywordListId,
        index: u16.make(keywordsListIndex),
      }));
    }

    artistList.push(Artist.create({
      uniqueId: AsciiString20.make(artist.uniqueId),
      keywords: artistKeywordRefs,
    }));
  }

  return [
    ArtistList.create({ artists: artistList }),
    KeywordList.create({ listId: keywordListId, keywords: keywordList }),
  ];
}

export function generateBrandSearchElements(brandProfiles: BrandProfile[]): SnapshotElement[] {
  const brandList = new Array<Brand>();
  const keywordList = new Array<AsciiString120>();
  const keywordListId = u8.make(3);
  let keywordsListIndex = -1;

  for(const brand of brandProfiles) {
    const name = brand.findComponentField<LocalizedNameComponent, LocalizedString>(
        LocalizedNameComponent.TYPE_ID, (c) => c.names);

    if(name == null) {
      continue;
    }

    const brandKeywordRefs = new Array<KeywordRef>();

    const keywords = generateKeywordsFromLocalizedString(name);
    for(const keyword of keywords) {
      keywordsListIndex++;
      keywordList.push(keyword);

      brandKeywordRefs.push(KeywordRef.create({
        listId: keywordListId,
        index: u16.make(keywordsListIndex),
      }));
    }

    brandList.push(Brand.create({
      uniqueId: AsciiString20.make(brand.uniqueId),
      keywords: brandKeywordRefs,
    }));
  }

  return [
    BrandList.create({ brands: brandList }),
    KeywordList.create({ listId: keywordListId, keywords: keywordList }),
  ];
}

export function generateOrganizerSearchElements(organizerProfiles: OrganizerProfile[]): SnapshotElement[] {
  const organizerList = new Array<Organizer>();
  const keywordList = new Array<AsciiString120>();
  const keywordListId = u8.make(4);
  let keywordsListIndex = -1;

  for(const organizer of organizerProfiles) {
    const name = organizer.findComponentField<LocalizedNameComponent, LocalizedString>(
        LocalizedNameComponent.TYPE_ID, (c) => c.names);

    if(name == null) {
      continue;
    }

    const organizerKeywordRefs = new Array<KeywordRef>();

    const keywords = generateKeywordsFromLocalizedString(name);
    for(const keyword of keywords) {
      keywordsListIndex++;
      keywordList.push(keyword);

      organizerKeywordRefs.push(KeywordRef.create({
        listId: keywordListId,
        index: u16.make(keywordsListIndex),
      }));
    }

    organizerList.push(Organizer.create({
      uniqueId: AsciiString20.make(organizer.uniqueId),
      keywords: organizerKeywordRefs,
    }));
  }

  return [
    OrganizerList.create({ organizers: organizerList }),
    KeywordList.create({ listId: keywordListId, keywords: keywordList }),
  ];
}

export function generateKeywordsFromLocalizedString(
    localizedString: LocalizedString,
): AsciiString120[] {
  const result: AsciiString120[] = [];

  for(const language of Object.values(Language)) {
    if(localizedString.hasAndNotEmpty(language)) {
      const value = localizedString.get(language);
      if(isNullOrUndefinedOrEmpty(value)) {
        continue;
      }

      const sanitizedValue = sanitizeStringToAscii120(value);
      if(isNullOrUndefined(sanitizedValue)) {
        continue;
      }

      result.push(sanitizedValue);
    }
  }

  return result;
}

export function sanitizeStringToAscii120(value: string): AsciiString120 | null {
  value = diacritics.remove(value);
  const validChars: string[] = [];

  let j = 0;
  for(let i = 0; i < value.length && j < 120; i++) {
    if(value.charCodeAt(i) < 32 || value.charCodeAt(i) > 127) {
      continue;
    }

    validChars.push(value.charAt(i));
    j++;
  }

  if(validChars.length <= 0) {
    return null;
  }

  return AsciiString120.makeOrNull(validChars.join(''));
}
